// Minimal hand-rolled URL sync, not react-router: netdoc has no
// page-per-route component tree — every "page" is a slice of one
// view-model built by useCore() + src/screens/*.js (see
// docs/architecture.md) — so react-router's <Routes>/<Route> model doesn't
// fit without rewriting that whole pattern. Instead, the handful of state
// fields that already fully determine "which screen" (s.screen,
// s.articleId, s.courseId, s.moduleIdx, s.calcTab, s.compareId,
// s.diagramId, s.graphFocus, s.certifId) get mapped to/from a URL here,
// and useCore wires a single effect to keep the two in sync — see
// syncUrlEffect's callers in useCore.js. Every go*()/nav()/patch() call
// site elsewhere is unchanged.

// screen -> path. Falls through to "/" for "home" and any screen this
// table doesn't know about, so a future screen without a route entry just
// doesn't get one (no crash).
export function pathFor(s) {
  switch (s.screen) {
    case "article":
      return "/fiche/" + encodeURIComponent(s.articleId);
    case "course":
      return "/cours/" + encodeURIComponent(s.courseId) + "/" + (s.moduleIdx + 1);
    case "index":
      return "/index";
    case "tools":
      return "/outils/" + encodeURIComponent(s.calcTab);
    case "tshoot":
      return "/depannage";
    case "xlate":
      return "/traducteur";
    case "compare":
      return "/comparatifs/" + encodeURIComponent(s.compareId);
    case "diagrams":
      return "/schemas/" + encodeURIComponent(s.diagramId);
    case "graph":
      return "/graphe/" + encodeURIComponent(s.graphFocus);
    case "revise":
      return "/revision";
    case "certif":
      return "/certif/" + encodeURIComponent(s.certifId);
    case "auth":
      return "/connexion";
    case "profile":
      return "/profil";
    default:
      return "/";
  }
}

// path -> state patch. Returns null for a path this app doesn't recognize
// at all (caller shows the 404 screen); a recognized path with a bad/
// unknown id (e.g. /fiche/does-not-exist) still returns a patch — every
// screen builder already falls back gracefully (ARTICLES.find(...) ||
// ARTICLES[0], same pattern for courses/exams/comparatifs/schémas/graphe),
// so there's no separate validation to duplicate here.
export function parsePath(pathname) {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean).map(decodeURIComponent);
  if (parts.length === 0) return { screen: "home" };
  const [head, ...rest] = parts;
  switch (head) {
    case "fiche":
      return rest[0] ? { screen: "article", articleId: rest[0] } : null;
    case "cours": {
      if (!rest[0]) return { screen: "course" };
      const n = rest[1] ? parseInt(rest[1], 10) : 1;
      const moduleIdx = Number.isFinite(n) ? Math.max(0, n - 1) : 0;
      return { screen: "course", courseId: rest[0], moduleIdx };
    }
    case "index":
      return { screen: "index" };
    case "outils":
      return { screen: "tools", ...(rest[0] ? { calcTab: rest[0] } : {}) };
    case "depannage":
      return { screen: "tshoot" };
    case "traducteur":
      return { screen: "xlate" };
    case "comparatifs":
      return { screen: "compare", ...(rest[0] ? { compareId: rest[0] } : {}) };
    case "schemas":
      return { screen: "diagrams", ...(rest[0] ? { diagramId: rest[0] } : {}) };
    case "graphe":
      return { screen: "graph", ...(rest[0] ? { graphFocus: rest[0] } : {}) };
    case "revision":
      return { screen: "revise" };
    case "certif":
      return { screen: "certif", ...(rest[0] ? { certifId: rest[0] } : {}) };
    case "connexion":
      return { screen: "auth" };
    case "profil":
      return { screen: "profile" };
    default:
      return null;
  }
}
